import {
  type MotionValue,
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'framer-motion';
import React, { useRef } from 'react';

/**
 * How dim a line is before it is reached. Low enough to read as unlit, high
 * enough that the list is still legible as a shape, so the reader sees what
 * is coming rather than a blank.
 */
const UNREAD_OPACITY = 0.22;

/**
 * Where the fill starts and finishes, as the list's own top passes down the
 * viewport. It completes before the list reaches the middle of the screen,
 * so the lines are fully lit by the time anyone settles on them.
 */
const REVEAL_OFFSET = ['start 0.95', 'start 0.45'] as const;

interface LineProps {
  children: string;
  className?: string;
  progress: MotionValue<number>;
  /** Slice of the list's progress over which this line fills */
  range: [number, number];
}

/**
 * ### Line
 *
 * One line, filling across its own slice of the list's scroll. Its own
 * component because the opacity is a hook, and a hook cannot live in a loop.
 *
 * @returns The rendered list item
 */
function Line({ children, className, progress, range }: LineProps) {
  const opacity = useTransform(progress, range, [UNREAD_OPACITY, 1]);

  return (
    <motion.li className={className} style={{ opacity }}>
      {children}
    </motion.li>
  );
}

interface ScrollRevealLinesProps {
  lines: ReadonlyArray<string>;
  className?: string;
  /** Classes for each line */
  lineClassName?: string;
}

/**
 * ### ScrollRevealLines
 *
 * A list that fills a line at a time as it is scrolled to. Where
 * {@link ScrollRevealText} lights a paragraph word by word, this takes the
 * whole sentence as the unit: these lines are separate claims read one after
 * another, and lighting them a word at a time would break each of them in
 * the middle.
 *
 * One scroll progress drives the whole list, so the lines fill in order
 * rather than each waiting for its own turn in the viewport. Under a
 * reduced-motion preference the list is simply set in full, which is also
 * what a reader without scripting and any crawler sees.
 *
 * @returns The rendered list
 */
export default function ScrollRevealLines({
  lines,
  className,
  lineClassName,
}: ScrollRevealLinesProps) {
  const container = useRef<HTMLUListElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: container,
    offset: [...REVEAL_OFFSET],
  });

  if (shouldReduceMotion) {
    return (
      <ul ref={container} className={className}>
        {lines.map((line) => (
          <li key={line} className={lineClassName}>
            {line}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul ref={container} className={className}>
      {lines.map((line, index) => (
        <Line
          key={line}
          className={lineClassName}
          progress={scrollYProgress}
          range={[index / lines.length, (index + 1) / lines.length]}
        >
          {line}
        </Line>
      ))}
    </ul>
  );
}
